import { Signal } from '@angular/core';
import { FabricImage, FabricObject } from 'fabric';
import { ITransferDynamicContentSlide } from '@pcp/streaming';
import { computeBackgroundScale } from 'shared/util-yjs-fabric';
import { BandedObject, SlideCapability, SlideHostContext } from '../slide-capability';
import { DynamicContentSource } from './dynamic-content-source';

/** Fabric image carrying the binding's "never sync this" flag (it's a projection, not a drawing). */
type EphemeralImage = FabricImage & { _yjsEphemeral?: boolean };

/**
 * GraphicOverlay — renders graphic-textboard (`SlideType.graphicboard` = `XG`) dynamic content as
 * stacked full-frame images, a {@link SlideCapability} for the `canvas-renderer-composition` host.
 *
 * Each appended `graphic` item (from the per-layer dynamic-content channel, passed DOWN as a signal —
 * Decision C13/C14, capabilities never inject `ClassroomFacade`) becomes one fabric image drawn at
 * (0,0) and scaled by the SAME {@link BackgroundFit} rule as the slide's background
 * ({@link computeBackgroundScale}, read reactively from the host coordinate space), contributed in
 * the **content** z-band (below drawings). By design the source images are authored
 * background-sized with a transparent background, so applying the background's own fit makes the
 * overlay pixel-coincident with the background: on a fixed slide it fills the 1280×720 frame; on a
 * panorama it fills the full scaled world (larger than the viewport) and pans with the background,
 * hotspots, and drawings — rather than being squashed into the viewport window. Earlier items stay
 * visible beneath later ones (append order). No paging, no auto-fit — the simple twin of
 * `TextboardContent`; both ride the shared {@link DynamicContentSource}.
 *
 * Objects are `_yjsEphemeral` (the drawing binding must not sync them) but NOT `excludeFromExport`
 * (overlays are content and belong in exports; they export at display resolution — per-item HIGH-variant
 * export is the deferred cross-cutting image-quality concern). Content-band z-order below the drawings
 * is enforced by the host's band re-assertion + the binding's `afterReconcile` hook.
 */
export class GraphicOverlay implements SlideCapability {
    private readonly source: DynamicContentSource<ITransferDynamicContentSlide>;

    constructor(
        private readonly ctx: SlideHostContext,
        private readonly content: Signal<ITransferDynamicContentSlide[]>,
    ) {
        this.source = new DynamicContentSource<ITransferDynamicContentSlide>(
            item => this.createImage(item),
            item => item.imageResource ?? '',
            () => this.ctx.canvas.requestRenderAll(),
        );
    }

    /** Content is read live from the signal in {@link contributeObjects}; nothing to seed per slide. */
    onSlideChange(): void {
        // no-op
    }

    contributeObjects(): BandedObject[] {
        this.source.reconcile(this.content());
        return this.source.objects().map(object => ({ object, band: 'content' as const }));
    }

    private async createImage(item: ITransferDynamicContentSlide): Promise<FabricObject | null> {
        const url = item.imageResource;
        if (!url) return null;
        const img = (await FabricImage.fromURL(url, { crossOrigin: 'anonymous' })) as EphemeralImage;
        img.set({
            left: 0,
            top: 0,
            originX: 'left',
            originY: 'top',
            selectable: false,
            evented: false,
            objectCaching: false,
            excludeFromExport: false,
        });
        // Full-frame: the extension is authored at the background's dimensions, so scale it by the
        // SAME background-fit rule (shared helper → cannot drift from the actual background). On a
        // panorama this fills the scaled world and pans with it; on a fixed slide it fills the frame.
        // Canvas dims are read the same way the background is applied (`getWidth/getHeight`).
        if (img.width && img.height) {
            const { scaleX, scaleY } = computeBackgroundScale(
                this.ctx.coordinateSpace().fit,
                { width: img.width, height: img.height },
                { width: this.ctx.canvas.getWidth(), height: this.ctx.canvas.getHeight() },
            );
            img.scaleX = scaleX;
            img.scaleY = scaleY;
        }
        img._yjsEphemeral = true;
        return img;
    }

    dispose(): void {
        for (const object of this.source.objects()) this.ctx.canvas.remove(object);
        this.source.dispose();
    }
}
